<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters } from 'vuex'
import dayjs from '@/services/date'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import { camelCase, flow, upperFirst } from 'lodash'
import UserAvatar from '@/components/user/user-avatar.vue'
import { sortDate } from '@/helpers/date-helpers'
import BookingStatusLabel from '@/components/booking/booking-status-label.vue'
import BookingStatusMap from '@/shared/constants/booking/BookingStatusMap'
import BookingPendingApprovalsListErrorBlock from './booking-pending-approvals-list-error-block.vue'
import BookingPendingApprovalsListNonConsecutiveDatesShape from './booking-pending-approvals-list-non-consecutive-dates-shape.vue'

export default {
  name: 'BookingPendingApprovalsList',
  components: {
    UserAvatar,
    BookingPendingApprovalsListErrorBlock,
    BookingPendingApprovalsListNonConsecutiveDatesShape,
    BookingStatusLabel,
  },
  mixins: [baseMixin],
  data: () => {
    return {
      tableHeaders: [
        {
          text: 'Date',
          align: 'start',
          value: 'details.earliestDate',
          width: '20%',
        },
        {
          text: 'Days',
          align: 'center',
          value: 'days',
          width: '8%',
        },
        { text: 'Location', value: 'location.name' },
        { text: 'Covering', value: 'details.covering.name' },
        { text: 'Created By', value: 'createdOnUtc', sort: sortDate },
        { text: 'Status', value: 'status', align: 'center' },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ],
      failedToLoad: false,
      pendingApprovals: [],
    }
  },
  computed: {
    ...mapGetters({
      isLoadingApprovalsList: 'bookingApprovals/isLoadingApprovalsList',
    }),
  },
  async created() {
    await this.loadApprovals()
  },
  methods: {
    ...mapActions({
      loadPendingApprovals: 'bookingApprovals/loadPendingApprovals',
    }),
    loadBookingDetails(item) {
      this.$emit('loadBookingDetails', item)
    },
    resetLoadFailureState() {
      this.failedToLoad = false
    },
    async loadApprovals() {
      this.resetLoadFailureState()
      const result = await this.loadPendingApprovals()

      if (!result.isSuccess) {
        this.failedToLoad = true
      }

      this.pendingApprovals = result.data
    },
    bookingDatesString(earliest, latest) {
      const earliestDate = dayjs(earliest).format(DateFormatToken.LL)

      // Check if request only contains a single date
      if (earliest === latest) {
        return earliestDate
      }

      const latestDate = dayjs(latest).format(DateFormatToken.LL)

      return `${earliestDate} - ${latestDate}`
    },
    bookingTimeString(start, end) {
      return `${dayjs(start, DateFormatToken.HHmm).format(
        DateFormatToken.LT
      )} - ${dayjs(end, DateFormatToken.HHmm).format(DateFormatToken.LT)}`
    },
    clientAndGroupString(clientName, groupName) {
      return `${clientName}${groupName ? ` - ${groupName}` : groupName}`
    },
    createdDate(date) {
      return dayjs().to(date)
    },
    pascal: flow(camelCase, upperFirst),
    /**
     * The table expects a function to return the item class.
     */
    getTableItemClass() {
      return 'cursor-pointer'
    },
    getClientGroupName(item) {
      return item.clientGroup?.name ?? ''
    },
    getBookingStatus(item) {
      return BookingStatusMap[item.status]
    },
  },
}
</script>

<template>
  <div>
    <div v-if="failedToLoad">
      <BookingPendingApprovalsListErrorBlock @retry="loadApprovals" />
    </div>
    <v-data-table
      v-else
      :headers="tableHeaders"
      :items="pendingApprovals"
      :loading="isLoadingApprovalsList"
      :class="['rounded-xl custom-elevation pt-2']"
      :item-class="getTableItemClass"
      :footer-props="{
        itemsPerPageOptions: [5, 10, 15, 20],
      }"
      item-key="bookingId"
      data-testid="booking-pending-approvals-table"
      @click:row="loadBookingDetails"
    >
      <template v-slot:[`item.details.earliestDate`]="{ item }">
        <div class="d-flex">
          <div>
            <div class="d-flex">
              <p class="mb-0 font-weight-medium">
                {{
                  bookingDatesString(
                    item.details.earliestDate,
                    item.details.latestDate
                  )
                }}
              </p>
              <div style="position: relative; top: 3px" class="ml-2">
                <BookingPendingApprovalsListNonConsecutiveDatesShape
                  v-if="!item.details.areDatesConsecutive"
                />
              </div>
            </div>
            <p class="mb-0 text-caption">{{
              bookingTimeString(item.details.startTime, item.details.endTime)
            }}</p>
          </div>
        </div>
      </template>

      <template v-slot:[`item.days`]="{ item }">
        <div class="text-center"> {{ item.days }} </div>
      </template>

      <template v-slot:[`item.location.name`]="{ item }">
        <p class="mb-0 font-weight-medium">{{ item.location.name }}</p>
        <p class="mb-0 text-caption">{{
          clientAndGroupString(item.client.name, getClientGroupName(item))
        }}</p>
      </template>

      <template v-slot:[`item.details.covering.name`]="{ item }">
        <div class="d-flex align-center">
          <UserAvatar size="sm" :name="item.details.covering.name" />
          <div class="ml-2">
            <p class="mb-0 font-weight-medium">{{
              item.details.covering.name
            }}</p>
            <p class="mb-0 caption">{{ item.details.covering.reason }}</p>
          </div>
        </div>
      </template>

      <template v-slot:[`item.createdOnUtc`]="{ item }">
        <p class="mb-0 font-weight-medium">{{ item.createdBy.name }}</p>
        <p class="mb-0 text-caption">{{ createdDate(item.createdOnUtc) }}</p>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <BookingStatusLabel class="mx-auto" :status="getBookingStatus(item)" />
      </template>

      <template v-slot:[`item.actions`] class="text-align-right">
        <v-icon size="20" class="opacity-7">mdi-chevron-right</v-icon>
      </template>
    </v-data-table>
  </div>
</template>
